import { useCallback, useEffect, useMemo, useState } from 'react';
import type {
  HouseholdSharingStatus,
  SharedHouseholdDevice,
} from '../application/household-sharing';
import { useI18n } from '../i18n/I18nContext';
import { useApp } from './AppContext';
import { Button, Field, Icon } from './components';
import { householdSharingErrorMessageKey } from './household-sharing-errors';
import { AccountContactPanel } from './AccountContactPanel';

const initialStatus: HouseholdSharingStatus = {
  state: 'standalone',
  reason: 'not-configured',
};

export const HouseholdSharingPanel = ({ onboarding = false }: { readonly onboarding?: boolean }) => {
  const { sharing, snapshot, synchronizeHousehold } = useApp();
  const { t } = useI18n();
  const [status, setStatus] = useState<HouseholdSharingStatus>(initialStatus);
  const [checkingAccess, setCheckingAccess] = useState(true);
  const [devices, setDevices] = useState<readonly SharedHouseholdDevice[]>([]);
  const [accessMode, setAccessMode] = useState<'login' | 'recover' | 'signup'>('signup');
  const [accountHandle, setAccountHandle] = useState('');
  const [accountEmail, setAccountEmail] = useState('');
  const [passphrase, setPassphrase] = useState('');
  const [passphraseConfirmation, setPassphraseConfirmation] = useState('');
  const [deletionPassphrase, setDeletionPassphrase] = useState('');
  const [recoveryCode, setRecoveryCode] = useState('');
  const [newRecoveryCode, setNewRecoveryCode] = useState('');
  const [inviteUrl, setInviteUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [conflict, setConflict] = useState(false);
  const localHouseholdId = snapshot?.household?.id ?? null;

  const load = useCallback(async () => {
    const next = await sharing.status(localHouseholdId);
    setStatus(next);
    if (next.state === 'connected' && next.binding != null) {
      try {
        setDevices(await sharing.devices(next.binding.householdId));
      } catch {
        setDevices([]);
      }
    } else {
      setDevices([]);
    }
  }, [localHouseholdId, sharing]);

  useEffect(() => {
    let cancelled = false;
    const hydrate = async () => {
      const next = await sharing.status(localHouseholdId);
      if (cancelled) return;
      setStatus(next);
      setCheckingAccess(false);
      if (next.state === 'connected' && next.binding != null) {
        try {
          const nextDevices = await sharing.devices(next.binding.householdId);
          if (!cancelled) setDevices(nextDevices);
        } catch {
          if (!cancelled) setDevices([]);
        }
      } else {
        setDevices([]);
      }
    };
    void hydrate();
    return () => { cancelled = true; };
  }, [localHouseholdId, sharing]);

  const run = async (action: () => Promise<void>) => {
    if (busy) return;
    setBusy(true);
    setMessage(null);
    try {
      await action();
    } catch (error) {
      setMessage(t(householdSharingErrorMessageKey(error)));
    } finally {
      setBusy(false);
    }
  };

  const synchronize = async (preferRemote = false) => {
    if (snapshot == null) return;
    if (snapshot.household == null) {
      setMessage(t('sharing.localHouseholdRequired'));
      return;
    }
    await run(async () => {
      const result = await synchronizeHousehold(preferRemote);
      if (result == null) {
        setMessage(t('sharing.unavailable'));
        return;
      }
      if (result.state === 'remote') {
        setConflict(false);
        setMessage(t('sharing.received'));
      } else if (result.state === 'published') {
        setConflict(false);
        setMessage(t('sharing.published'));
      } else if (result.state === 'unchanged') {
        setConflict(false);
        setMessage(t('sharing.unchanged'));
      } else if (result.state === 'awaiting_key') {
        setMessage(t('sharing.awaitingOwner'));
      } else if (result.state === 'conflict') {
        setConflict(true);
        setMessage(t('sharing.conflict'));
      } else if (result.state === 'revoked') {
        setConflict(false);
        setMessage(t('sharing.revoked'));
      } else {
        setMessage(t('sharing.unavailable'));
      }
      await load();
    });
  };

  const memberSubjects = useMemo(
    () => [...new Set(devices.filter((device) => device.role === 'member').map((device) => device.productSubjectId))],
    [devices],
  );

  if ((!sharing.available && onboarding) ||
      (status.state === 'standalone' && (status.reason === 'demo' || onboarding && !checkingAccess))) return null;

  return (
    <section className={`settings-card sharing-card${onboarding ? ' sharing-card--onboarding' : ''}`} data-testid="household-sharing">
      <div className="settings-card__heading">
        <span className="settings-icon"><Icon name="home" /></span>
        <div>
          <h2>{t('sharing.title')}</h2>
          <p>{t('sharing.localFirst')}</p>
        </div>
      </div>

      {checkingAccess ? (
        <p role="status">{t('sharing.checkingAccess')}</p>
      ) : null}
      {!checkingAccess && status.state === 'standalone' ? (
        <div className="notice notice--neutral" role="status"><Icon name="info" /><p>{t('sharing.notActivated')}</p></div>
      ) : null}
      {status.state === 'unavailable' ? (
        <div className="notice notice--neutral" role="status"><Icon name="warning" /><p>{t('sharing.unavailable')}</p></div>
      ) : null}
      {status.state === 'revoked' ? (
        <div className="notice notice--warning" role="status"><Icon name="warning" /><p>{t('sharing.revoked')}</p></div>
      ) : null}

      {status.state === 'disconnected' ? (
        <div className="sharing-card__stack">
          <Field
            label={t('sharing.accountHandle')}
            hint={t('sharing.accountHandleHint')}
            autoComplete="username"
            value={accountHandle}
            onChange={(event) => setAccountHandle(event.target.value)}
          />
          {accessMode === 'signup' ? (
            <Field
              autoComplete="email"
              inputMode="email"
              label={t('accountContact.email')}
              hint={t('accountContact.emailHint')}
              value={accountEmail}
              onChange={(event) => setAccountEmail(event.target.value)}
            />
          ) : null}
          {accessMode === 'recover' ? (
            <>
              <Field
                label={t('sharing.recoveryCode')}
                hint={t('sharing.recoveryCodeHint')}
                autoComplete="off"
                value={recoveryCode}
                onChange={(event) => setRecoveryCode(event.target.value)}
              />
              <div className="notice notice--neutral">
                <Icon name="info" />
                <p>{t('sharing.recoveryScope')}</p>
              </div>
            </>
          ) : null}
          <Field
            label={accessMode === 'recover' ? t('sharing.newPassphrase') : t('sharing.passphrase')}
            hint={t('sharing.passphraseHint')}
            type="password"
            autoComplete={accessMode === 'login' ? 'current-password' : 'new-password'}
            value={passphrase}
            onChange={(event) => setPassphrase(event.target.value)}
          />
          {accessMode !== 'login' ? (
            <Field
              label={t('sharing.passphraseConfirmation')}
              type="password"
              autoComplete="new-password"
              value={passphraseConfirmation}
              onChange={(event) => setPassphraseConfirmation(event.target.value)}
            />
          ) : null}
          <Button disabled={busy || accountHandle.trim() === '' || passphrase.length < 12 ||
              (accessMode === 'signup' && !accountEmail.includes('@')) ||
              (accessMode !== 'login' && passphrase !== passphraseConfirmation) ||
              (accessMode === 'recover' && recoveryCode.trim() === '')} onClick={() => void run(async () => {
            if (accessMode === 'signup') {
              const created = await sharing.signup(
                accountHandle, accountEmail, passphrase, passphraseConfirmation,
              );
              setNewRecoveryCode(created.recoveryCode);
              setStatus(created.status);
              setMessage(t('sharing.accountCreated'));
              setAccountEmail('');
            } else if (accessMode === 'login') {
              setStatus(await sharing.login(accountHandle, passphrase));
              setMessage(t('sharing.accountConnected'));
            } else {
              const recovered = await sharing.recover(
                accountHandle, recoveryCode, passphrase, passphraseConfirmation,
              );
              setNewRecoveryCode(recovered.recoveryCode);
              setStatus(recovered.status);
              setRecoveryCode('');
              setMessage(t('sharing.accountRecovered'));
            }
            setPassphrase('');
            setPassphraseConfirmation('');
          })} icon={<Icon name="arrow" />}>
            {t(`sharing.${accessMode}`)}
          </Button>
          <div className="button-grid">
            {accessMode !== 'signup' ? (
              <Button variant="ghost" disabled={busy} onClick={() => setAccessMode('signup')}>
                {t('sharing.useSignup')}
              </Button>
            ) : null}
            {accessMode !== 'login' ? (
              <Button variant="ghost" disabled={busy} onClick={() => setAccessMode('login')}>
                {t('sharing.useLogin')}
              </Button>
            ) : null}
            {accessMode !== 'recover' ? (
              <Button variant="ghost" disabled={busy} onClick={() => setAccessMode('recover')}>
                {t('sharing.useRecovery')}
              </Button>
            ) : null}
          </div>
        </div>
      ) : null}

      {newRecoveryCode !== '' ? (
        <div className="sharing-card__invite" role="status">
          <div className="notice notice--warning">
            <Icon name="warning" />
            <p>{t('sharing.recoverySave')}</p>
          </div>
          <Field label={t('sharing.recoveryCode')} readOnly value={newRecoveryCode} />
          <Button variant="ghost" disabled={busy} onClick={() => void run(async () => {
            await navigator.clipboard.writeText(newRecoveryCode);
            setMessage(t('sharing.recoveryCopied'));
          })}>{t('sharing.copyRecovery')}</Button>
        </div>
      ) : null}

      {status.state === 'connected' && status.binding == null ? (
        <div className="sharing-card__stack">
          <div className="notice notice--success" role="status"><Icon name="check" /><p>{t('sharing.accountReady')}</p></div>
          {snapshot?.household != null && status.households.length === 0 ? (
            <Button disabled={busy} onClick={() => void run(async () => {
              setStatus(await sharing.create(snapshot));
              setMessage(t('sharing.created'));
              await load();
            })}>
              {t('sharing.create')}
            </Button>
          ) : null}
          <Button variant="secondary" disabled={busy} onClick={() => void run(async () => {
            setStatus(await sharing.join());
            setMessage(t('sharing.joined'));
            await load();
          })}>
            {t('sharing.join')}
          </Button>
          <Button variant="ghost" disabled={busy} onClick={() => void run(async () => {
            await sharing.disconnect();
            await load();
          })}>{t('sharing.disconnect')}</Button>
        </div>
      ) : null}

      {status.state === 'connected' && status.binding != null ? (
        <div className="sharing-card__stack">
          <div className={`notice ${status.binding.keyReady ? 'notice--success' : 'notice--info'}`} role="status">
            <Icon name={status.binding.keyReady ? 'check' : 'clock'} />
            <div>
              <p>{status.binding.keyReady ? t('sharing.encryptedReady') : t('sharing.awaitingOwner')}</p>
              <small>{t('sharing.memberCount', {
                count: status.households.find((item) => item.householdId === status.binding?.householdId)?.memberCount ?? devices.length,
              })}</small>
            </div>
          </div>

          <Button disabled={busy} onClick={() => void synchronize()} icon={<Icon name="arrow" />}>
            {t('sharing.sync')}
          </Button>
          {conflict ? (
            <Button variant="secondary" disabled={busy} onClick={() => {
              if (window.confirm(t('sharing.remoteConfirm'))) void synchronize(true);
            }}>{t('sharing.useRemote')}</Button>
          ) : null}

          {status.binding.role === 'owner' ? (
            <>
              <Button variant="secondary" disabled={busy} onClick={() => void run(async () => {
                const invite = await sharing.invitationLink(status.binding!.householdId);
                setInviteUrl(invite.url);
              })}>{t('sharing.invite')}</Button>
              {inviteUrl !== '' ? (
                <div className="sharing-card__invite">
                  <Field label={t('sharing.inviteLink')} readOnly value={inviteUrl} />
                  <Button variant="ghost" disabled={busy} onClick={() => void run(async () => {
                    await navigator.clipboard.writeText(inviteUrl);
                    setMessage(t('sharing.inviteCopied'));
                  })}>{t('sharing.copy')}</Button>
                </div>
              ) : null}
              <Button variant="ghost" disabled={busy} onClick={() => void run(async () => {
                await sharing.authorizeDevices(status.binding!.householdId);
                setMessage(t('sharing.authorized'));
                await load();
              })}>{t('sharing.authorize')}</Button>
              {memberSubjects.map((subject, index) => (
                <div className="sharing-card__member" key={subject}>
                  <span>{t('sharing.memberLabel', { number: index + 2 })}</span>
                  <Button variant="ghost" disabled={busy} onClick={() => {
                    if (!snapshot || !window.confirm(t('sharing.revokeConfirm'))) return;
                    void run(async () => {
                      await sharing.revokeMember(status.binding!.householdId, subject, snapshot);
                      setMessage(t('sharing.memberRevoked'));
                      await load();
                    });
                  }}>{t('sharing.revokeMember')}</Button>
                </div>
              ))}
            </>
          ) : null}

          <Button variant="ghost" disabled={busy} onClick={() => {
            if (!window.confirm(t('sharing.deviceRevokeConfirm'))) return;
            void run(async () => {
              await sharing.revokeCurrentDevice();
              setMessage(t('sharing.deviceRevoked'));
              await load();
            });
          }}>{t('sharing.revokeDevice')}</Button>
          <Button variant="ghost" disabled={busy} onClick={() => void run(async () => {
            await sharing.disconnect();
            await load();
          })}>{t('sharing.disconnect')}</Button>
        </div>
      ) : null}

      {status.state === 'connected' ? (
        <div className="sharing-card__stack">
          <div className="notice notice--warning">
            <Icon name="warning" />
            <p>{t('sharing.deleteAccountWarning')}</p>
          </div>
          <Field
            label={t('sharing.deleteAccountPassphrase')}
            type="password"
            autoComplete="current-password"
            value={deletionPassphrase}
            onChange={(event) => setDeletionPassphrase(event.target.value)}
          />
          <Button variant="ghost" disabled={busy || deletionPassphrase.length < 12} onClick={() => {
            if (!window.confirm(t('sharing.deleteAccountConfirm'))) return;
            void run(async () => {
              await sharing.deleteAccount(deletionPassphrase);
              setDeletionPassphrase('');
              setNewRecoveryCode('');
              setStatus({ state: 'disconnected' });
              setMessage(t('sharing.accountDeleted'));
            });
          }}>{t('sharing.deleteAccount')}</Button>
        </div>
      ) : null}

      {status.state === 'connected' ? <AccountContactPanel /> : null}

      {message != null ? <p className="import-message" role="status">{message}</p> : null}
      <p className="microcopy">{t('sharing.encryption')}</p>
    </section>
  );
};
